import React, { useCallback, useEffect, useState } from 'react';

import {
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  keyframes,
  Select,
  Text,
  Textarea,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { IconType } from 'react-icons';
import { FiCheckCircle, FiFrown, FiMeh, FiSmile } from 'react-icons/fi';

import {
  DocumentationChapter,
  DocumentationFeedback as Feedback,
  DocumentationFeedbackRating,
  getDocumentationFeedback,
  saveDocumentationFeedback,
} from 'api/documentation';

import { tocHeadings } from './DocumentationToc';
import { chapterPath } from './utils';

export const MAX_FEEDBACK_COMMENT = 2000;
const fadeIn = keyframes`from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; }`;
/** The select's value for feedback about the whole page. */
const WHOLE_PAGE = '';

const RATINGS: {
  value: DocumentationFeedbackRating;
  label: string;
  icon: IconType;
}[] = [
  { value: 'helpful', label: 'Helpful', icon: FiSmile },
  { value: 'neutral', label: 'Neutral', icon: FiMeh },
  { value: 'unhelpful', label: 'Not helpful', icon: FiFrown },
];

/**
 * The reader's saved answers for a page, keyed by section ('' = whole page).
 * Loaded once per page and shared by every feedback widget on it.
 */
export const useDocumentationFeedback = (
  chapter?: DocumentationChapter,
  revision?: string,
) => {
  const path = chapter && chapterPath(chapter);
  const [answers, setAnswers] = useState<Record<string, Feedback>>({});

  useEffect(() => {
    setAnswers({});
    if (!path) return;
    const controller = new AbortController();
    getDocumentationFeedback(path, controller.signal)
      .then(items =>
        setAnswers(
          Object.fromEntries(
            items.map(item => [item.section_id ?? WHOLE_PAGE, item]),
          ),
        ),
      )
      // Feedback is optional; a failed load just shows nothing selected.
      .catch(() => undefined);
    return () => controller.abort();
  }, [path]);

  const save = useCallback(
    async (
      sectionId: string,
      rating: DocumentationFeedbackRating,
      comment: string,
    ) => {
      if (!path || !revision) return;
      const saved = await saveDocumentationFeedback({
        page_path: path,
        section_id: sectionId || null,
        revision,
        rating,
        comment: comment.trim() || null,
      });
      setAnswers(current => ({ ...current, [sectionId]: saved }));
    },
    [path, revision],
  );

  return { answers, save };
};

interface Props {
  chapter: DocumentationChapter;
  answers: Record<string, Feedback>;
  onSave: (
    sectionId: string,
    rating: DocumentationFeedbackRating,
    comment: string,
  ) => Promise<void>;
  /** The section in view when a face is clicked; preselected in the form. */
  activeSectionId?: string;
  compact?: boolean;
}

/** "Was this helpful?" with faces, a section picker and an optional comment. */
export const DocumentationFeedback: React.FC<Props> = ({
  chapter,
  answers,
  onSave,
  activeSectionId,
  compact,
}) => {
  const sections = tocHeadings(chapter.headings ?? []);
  const [sectionId, setSectionId] = useState(WHOLE_PAGE);
  const [rating, setRating] = useState<DocumentationFeedbackRating>();
  const [comment, setComment] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>(
    'idle',
  );

  // Start fresh on every page.
  useEffect(() => {
    setIsOpen(false);
    setStatus('idle');
    setRating(undefined);
    setComment('');
    setSectionId(WHOLE_PAGE);
  }, [chapter.slug]);

  const showAnswer = (section: string) => {
    setSectionId(section);
    setComment(answers[section]?.comment ?? '');
  };

  const choose = (value: DocumentationFeedbackRating) => {
    if (!isOpen) {
      const section =
        activeSectionId && sections.some(item => item.id === activeSectionId)
          ? activeSectionId
          : WHOLE_PAGE;
      showAnswer(section);
    }
    setRating(value);
    setStatus('idle');
    setIsOpen(true);
  };

  const submit = async () => {
    if (!rating) return;
    setStatus('saving');
    try {
      await onSave(sectionId, rating, comment);
      setStatus('saved');
      setIsOpen(false);
    } catch {
      setStatus('error');
    }
  };

  const selected = isOpen ? rating : answers[WHOLE_PAGE]?.rating;

  return (
    <Box
      as="section"
      aria-label="Page feedback"
      borderTop={compact ? '1px solid' : undefined}
      borderColor="gray.100"
      pt={compact ? 5 : 0}
    >
      <HStack spacing={3} flexWrap="wrap">
        <Text fontSize={compact ? '14px' : '16px'} color="gray.600">
          Was this {compact ? 'helpful' : 'page helpful'}?
        </Text>
        <HStack spacing={1}>
          {RATINGS.map(({ value, label, icon }) => (
            <Tooltip key={value} label={label} openDelay={300}>
              <IconButton
                aria-label={label}
                aria-pressed={selected === value}
                icon={<Icon as={icon} boxSize={compact ? 4 : 5} />}
                size={compact ? 'sm' : 'md'}
                variant="unstyled"
                display="inline-flex"
                borderRadius="full"
                color={selected === value ? 'primary' : 'gray.400'}
                bg={selected === value ? 'selectedSideNavMenuBg' : undefined}
                _hover={{ color: 'primary' }}
                onClick={() => choose(value)}
              />
            </Tooltip>
          ))}
        </HStack>
      </HStack>

      {isOpen && (
        <VStack
          align="stretch"
          spacing={3}
          pt={4}
          animation={`${fadeIn} 150ms ease-out`}
        >
          <Box>
            <Text
              as="label"
              htmlFor={`feedback-section-${compact ? 'aside' : 'page'}`}
              fontSize="13px"
              color="gray.600"
            >
              About
            </Text>
            <Select
              id={`feedback-section-${compact ? 'aside' : 'page'}`}
              size="sm"
              mt={1}
              borderRadius={6}
              value={sectionId}
              onChange={event => showAnswer(event.target.value)}
            >
              <option value={WHOLE_PAGE}>Whole page</option>
              {sections.map(item => (
                <option key={item.id} value={item.id}>
                  {item.level === 3 ? `  ${item.text}` : item.text}
                </option>
              ))}
            </Select>
          </Box>
          <Textarea
            aria-label="Tell us more"
            placeholder="Tell us more (optional)"
            size="sm"
            borderRadius={6}
            rows={compact ? 3 : 4}
            maxLength={MAX_FEEDBACK_COMMENT}
            value={comment}
            onChange={event => setComment(event.target.value)}
          />
          <HStack justify="space-between">
            <Text fontSize="12px" color={status === 'error' ? 'red.500' : ''}>
              {status === 'error'
                ? "Couldn't send feedback. Try again."
                : comment.length > MAX_FEEDBACK_COMMENT - 200
                ? `${MAX_FEEDBACK_COMMENT - comment.length} characters left`
                : ''}
            </Text>
            <HStack spacing={2}>
              <Button
                size="sm"
                variant="ghost"
                fontWeight={400}
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                fontWeight={400}
                color="white"
                bg="primary"
                borderRadius={6}
                _hover={{ filter: 'brightness(0.95)' }}
                isLoading={status === 'saving'}
                onClick={submit}
              >
                Send
              </Button>
            </HStack>
          </HStack>
        </VStack>
      )}

      {status === 'saved' && !isOpen && (
        <HStack spacing={1.5} pt={2} color="green.600" fontSize="13px">
          <Icon as={FiCheckCircle} />
          <Text>Thanks for your feedback</Text>
        </HStack>
      )}
    </Box>
  );
};
